import { useRef, useMemo } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { useModalControl, KeyNames, useTabbable } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../../constants';
import { useViewEventOnCalendar } from '../../../hooks/calendar/use-view-event-on-calendar';
import { useGetAppointmentDetails } from '../../../hooks/use-get-appointment-details';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { OutOfOfficeEventCardModal } from '../../modals/out-of-office-event-card-modal';
import { PersonStatusIndicator } from '../../schedule-calendar-components/appointment-event-card/person-status-indicator';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { EventType } from '../types';
import {
  maxTotalGridColumns,
  zIndex,
  createGridColumnsFromStartAndEndTimestamp,
  getDurationFromHours,
  getEventCardCssForVerticalView,
} from '../utils';
import { useZoomSafeContext } from '../zoom/zoom-context';

type EventCardProps = {
  id: string;
  locationName: string;
  locationId?: string;
  providerName?: string;
  providerId?: string;
  title: string;
  startDate: string;
  calendarDate?: string;
  startHour: string;
  endHour: string;
  eventTypeColor?: string;
  laneCount?: number;
  laneIndex?: number;
  type: EventType;
  visibleLaneCount: number;
  personStatus?: string;
};

export const CalendarEventCardV2 = ({
  id,
  title,
  type,
  locationName,
  locationId,
  providerName,
  providerId,
  startDate,
  calendarDate,
  startHour,
  endHour,
  eventTypeColor,
  laneCount = 0,
  laneIndex = 0,
  visibleLaneCount,
  personStatus,
}: EventCardProps) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const { startHour: calendarStartHour, endHour: calendarEndHour, EventCardComponent } = useCalendarViewProps();
  const { setBackgroundView, setFloatingTooltipData } = useCalendarView([
    'setBackgroundView',
    'setFloatingTooltipData',
  ]);

  const eventCardContentWrapperRef = useRef<HTMLDivElement | null>(null);
  const { removeEventIdInUrl, isFocusedEvent } = useViewEventOnCalendar({
    selectedEventIds: [id],
    startHour,
    eventCardRef: eventCardContentWrapperRef,
  });

  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const isAppointment = type === 'appointment';
  const eventColor = eventTypeColor ?? (isAppointment ? PRIMARY_EVENT_COLOR : theme.colors.neutral50);

  const { triggerProps: outOfOfficeTriggerProps, modalProps: outOfOfficeModalProps } = useModalControl({
    onClose: () => collapseEventCard(),
  });
  const { openModal: openAppointmentModal, isSelected: isEventCardExpanded } = useGetAppointmentDetails({
    appointmentId: id,
    locationId: locationId ?? '',
    onModalClose: () => collapseEventCard(),
  });

  const expandEventCard = () => {
    if (isAppointment) {
      openAppointmentModal();
    } else {
      outOfOfficeTriggerProps.onClick();
      setBackgroundView(true);
    }
  };

  const collapseEventCard = () => {
    setBackgroundView(false);
    setFloatingTooltipData(undefined);
  };

  useTabbable({ ref: eventCardContentWrapperRef, autofocus: 'first' });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // need to ensure the event is fired from the event card wrapper div
    if (e.target !== e.currentTarget) {
      return;
    }
    if ((e.key === KeyNames.Enter || e.key === KeyNames.Space) && !isEventCardExpanded) {
      e.preventDefault();
      expandEventCard();
      eventCardContentWrapperRef.current?.focus();
    }
    if (e.key === KeyNames.Escape && isEventCardExpanded) {
      e.preventDefault();
      collapseEventCard();
    }
  };

  const { startGridColumn, endGridColumn, eventDuration } = createGridColumnsFromStartAndEndTimestamp(
    calendarStartHour,
    calendarEndHour,
    pixelsPerMinute
  )({
    startHourTimestamp: startHour,
    endHourTimeStamp: endHour,
  });

  const shouldHideEventCard = () => {
    const maxTotalMinutes = maxTotalGridColumns(calendarStartHour, calendarEndHour);

    return !(
      (startGridColumn <= 1 && endGridColumn <= 1) ||
      (startGridColumn === maxTotalMinutes && endGridColumn === maxTotalMinutes) ||
      // hide event card if it's outside of the visible lane count
      laneIndex >= visibleLaneCount
    );
  };

  return (
    <motion.div
      data-event='true'
      tabIndex={isEventCardExpanded ? -1 : 0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setFloatingTooltipData({ name: title, value: duration })}
      onMouseLeave={() => setFloatingTooltipData(undefined)}
      layout
      css={[
        !shouldHideEventCard() &&
          css`
            display: none;
          `,
        css`
          position: absolute;
          top: ${startGridColumn}px;
          height: ${eventDuration * pixelsPerMinute}px;
          transition: left 0.5s ease-in-out, width 0.5s ease-in-out;
        `,
        getEventCardCssForVerticalView(visibleLaneCount, laneCount, laneIndex),
        css`
          user-select: none;
          outline: none;
          &::after {
            content: '';
            width: calc(100% + 8px);
            height: 100%;
            position: absolute;
            left: 50%;
            top: 50%;
            pointer-events: none;
            border-radius: ${theme.borderRadius.medium};
            opacity: 0;
            transition: 0.2s ease;
            transform: translate(-50%, -50%);
            border: 2px solid ${theme.colors.primary30};
          }
          &:focus-visible {
            &::after {
              opacity: 1;
            }
          }
        `,
        !isEventCardExpanded &&
          css`
            cursor: pointer;
          `,
      ]}
      style={{ zIndex: zIndex.eventCardDefault + (isEventCardExpanded || isFocusedEvent ? 1 : 0) }}
    >
      <motion.article
        data-event-id={id}
        tabIndex={isEventCardExpanded ? 0 : -1}
        onKeyDown={handleKeyDown}
        data-trackingid={
          isAppointment ? CALENDAR_TRACKING_IDS.appointmentEventCard : CALENDAR_TRACKING_IDS.outOfOfficeEventCard
        }
        ref={eventCardContentWrapperRef}
        onMouseDown={(e) => e.stopPropagation()}
        layout={!isAppointment}
        onClick={() => {
          if (!isEventCardExpanded) {
            expandEventCard();
            removeEventIdInUrl();
          }
        }}
        css={[
          css`
            border-radius: ${theme.borderRadius.small};
            padding: ${theme.spacing(0.5, 0.5, 0, 1.5)};
            box-shadow: ${theme.shadows.light};
            position: relative;
            overflow: hidden;
            outline: none;
            background: ${isAppointment ? theme.colors.white : theme.colors.neutral20};
            height: 100%;
            transition: box-shadow 0.2s ease-in-out, scale 0.2s ease-in-out, outline 0.1s linear;
            &:hover {
              box-shadow: ${theme.shadows.floating};
              scale: 1.01;
            }
          `,
          (isEventCardExpanded || isFocusedEvent) &&
            css`
              outline: 2px solid ${theme.colors.primary50};
              outline-offset: 2px;
            `,
        ]}
      >
        <PersonStatusIndicator personStatus={personStatus} height={4} borderRadius='small' isExpanded={false} />

        <EventColoredLine eventColor={eventColor} expanded={isEventCardExpanded && !isAppointment} />
        <OutOfOfficeEventCardModal
          id={id}
          eventType={type}
          title={title}
          duration={duration}
          locationId={locationId}
          locationName={locationName}
          providerId={providerId}
          startDate={startDate}
          providerName={providerName}
          calendarDate={calendarDate}
          modalProps={outOfOfficeModalProps}
        />
        <EventCardComponent
          id={id}
          eventType={type}
          title={title}
          duration={duration}
          locationName={locationName}
          locationId={locationId}
          providerId={providerId}
          providerName={providerName}
          isExpanded={isEventCardExpanded}
          startDate={startDate}
        />
      </motion.article>
    </motion.div>
  );
};

const EventColoredLine = ({ eventColor, expanded }: { eventColor: string; expanded: boolean }) => {
  return (
    <motion.div
      layout='preserve-aspect'
      style={{
        position: 'absolute',
        background: eventColor,
        left: 0,
        top: 0,
        width: 4,
        height: '100%',
      }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      animate={{
        width: expanded ? 8 : 4,
      }}
    />
  );
};
